import { getSupabase, supabasePublic, supabaseSchema } from "../components/supabaseClient";
import { PATIENT_USER_SELECT } from "../config/roles";

function applyKey(query, { id, email }) {
  return id != null ? query.eq("id", id) : query.eq("email", email);
}

/**
 * Fetch the signed-in user's row carrying their AUTHORITATIVE role.
 *
 * A tenant-schema user (student/socialworker/other/medstudent) keeps their role
 * in their tenant-schema `users` row. The same person can still have a clinical
 * role in public.users — e.g. a doctor account that later enrolled as a student —
 * so reading the role straight from public.users renders them as a clinician and
 * exposes the whole clinic schema. When the active schema is a tenant schema we
 * therefore override the role with the value from that tenant-schema row.
 *
 * public.users is still read for everything else, because the patient_*
 * verification columns only exist there (and patients/clinicians live on public).
 *
 * @param {{ id?: string, email?: string }} key Lookup by user id or email.
 * @returns {Promise<{ data: any, error: any }>} Supabase-style result.
 */
export async function fetchEffectiveUserRow(key) {
  // Resolve the active schema before deciding where the authoritative role lives.
  const supabase = await getSupabase();

  const publicRes = await applyKey(
    supabasePublic().from("users").select(`timezone, ${PATIENT_USER_SELECT}`),
    key,
  ).single();

  const onTenantSchema =
    typeof supabaseSchema === "string" &&
    supabaseSchema.trim() !== "" &&
    supabaseSchema.toLowerCase() !== "public";

  if (!onTenantSchema || publicRes.error || !publicRes.data) {
    return publicRes;
  }

  // Tenant-schema users: the tenant-schema row holds the real role. Only the
  // role is read here — patient_* columns don't exist on tenant schemas, and a
  // tenant user is never a patient.
  try {
    const tenantRes = await applyKey(
      supabase.from("users").select("role"),
      key,
    ).single();
    if (!tenantRes.error && tenantRes.data && tenantRes.data.role) {
      return {
        data: { ...publicRes.data, role: tenantRes.data.role },
        error: null,
      };
    }
  } catch (_) {
    // Tenant-schema row unreadable (e.g. PostgREST cache miss) — keep public row.
  }
  return publicRes;
}
